import { QueryClient } from '@tanstack/react-query';

import { useGetKeywordQuestionCardCursorPageQuery } from '@/hooks/qna/mobile/qna.gql.impl';

import { QuestionAccessibleType } from '@/types/bff/gql-schema';
import { GetKeywordQuestionCardCursorPageQuery } from '@/types/bff/gql-schema';
import { EQuestionAccessibleType } from '@/types/search';
import { KeywordSearchQuestionVo } from '@/types/search';
import { displayedAt } from '@/utils/format';
import useGetKeywordQuestionCardInfiniteQuery from './useGetKeywordQuestionCardInfiniteQuery';

interface Props {
  keyword: string;
  size?: number;
  enabled?: boolean;
}

const QUESTION_SIZE = 20;

const questionAccessibleTypeEnumParser = (questionAccessibleType: QuestionAccessibleType): EQuestionAccessibleType => {
  switch (questionAccessibleType) {
    case QuestionAccessibleType.All:
      return EQuestionAccessibleType.All;
    case QuestionAccessibleType.DoctorsAndMe:
      return EQuestionAccessibleType.DoctorsAndMe;
    case QuestionAccessibleType.Me:
      return EQuestionAccessibleType.Me;
  }
};

const parseSearchResultQuestionVo = (
  data: Array<GetKeywordQuestionCardCursorPageQuery>
): Array<KeywordSearchQuestionVo> => {
  if (data) {
    return data
      .flatMap((page) => page.keywordSearchQuestionCardCursorPage.elements)
      .map((element) => {
        return {
          ...element,
          question: {
            ...element.question,
            accessibleType: questionAccessibleTypeEnumParser(element.question.accessibleType),
          },
          questionCreatedAt: displayedAt(element.question.createdAt),
        };
      });
  }
  return [] as Array<KeywordSearchQuestionVo>;
};

const useGetKeywordQuestionCardInfinite = ({ keyword, size = QUESTION_SIZE, enabled = true }: Props) => {
  const {
    data: searchedQnaData,
    isLoading: isSearchedQnaLoading,
    hasNextPage: hasSearchedQnaNextPage,
    fetchNextPage: fetchSearchedQnaNextPage,
  } = useGetKeywordQuestionCardInfiniteQuery(
    {
      request: {
        keyword,
        size,
      },
    },
    {
      select: (data) => {
        return {
          pages: parseSearchResultQuestionVo(data.pages),
          pageParams: data.pageParams,
        };
      },
      enabled: enabled && !!keyword && keyword.length > 0,
    }
  );

  const qnas = searchedQnaData?.pages.flatMap((qna) => qna) || [];

  return {
    qnas,
    searchedQnaData,
    isSearchedQnaLoading,
    hasSearchedQnaNextPage,
    fetchSearchedQnaNextPage,
  };
};

export default useGetKeywordQuestionCardInfinite;

const getNonBenefitHomeQnaParams = (keyword: string) => {
  return {
    keyword,
    size: 5,
  };
};

useGetKeywordQuestionCardInfinite.getNonBenefitHomeQnaParams = getNonBenefitHomeQnaParams;

const prefetchQuery = async (queryClient: QueryClient, params: Pick<Props, 'keyword'>) => {
  await queryClient.prefetchInfiniteQuery(
    useGetKeywordQuestionCardCursorPageQuery.getKey({
      request: {
        ...getNonBenefitHomeQnaParams(params.keyword),
      },
    }),
    useGetKeywordQuestionCardCursorPageQuery.fetcher({
      request: {
        ...getNonBenefitHomeQnaParams(params.keyword),
      },
    })
  );
};

useGetKeywordQuestionCardInfinite.prefetchQuery = prefetchQuery;
